import type { FlowSlotDto } from '@/types/dtos/flows/response/flow.dto';
import { Grid, Stack, IconButton, Tooltip } from '@mui/material';
import { Launch } from '@mui/icons-material';
import { useNavigate } from 'react-router';
import UpdateSessionForm from '@/components/events/detail/tabs/sessions/sessions-grid/update-session.form';
import { SessionActors } from '@/components/events/detail/tabs/sessions/session-actors/session-contributor-widget';
import { useFlowStore } from '@/state/stores/use-flow-store';
import { useEventStore } from '@/state/stores/use-event-store';
import { SessionPlaylistMediaWidget } from '@/components/sessions/session-playlist-media-widget';
import { SessionToggleLibrary } from '@/components/events/detail/tabs/sessions/sessions-grid/session-actions/session-toggle-library';
import { useFlowMode } from '../../use-flow-mode';
import { getObjectId } from '@/helpers/object.id.helper';
import { useGetSessionQuery } from '@/state/queries/sessions/use-get-session';
import { useTranslations } from '@/i18n/use-translations.tsx';
import { useMemo } from 'react';
import { ServerError } from '@/types/errors/server.error';
import { pickNewerSession } from '@/components/flows/detail/flow-detail.utils';
import type { ScheduleInlineDetailsTab } from '../schedule-inline-details-tab';
import type { ContributorListDisplayVariant } from '../../flow-detail.types';

export const UpdateSessionSlot = ({
  slot,
  onSessionPublishSuccess,
  showActorsAndMedia = true,
  inlineDetailsTab,
}: {
  slot: FlowSlotDto<'Session'>;
  onSessionPublishSuccess?: () => void;
  /** When false, hides MediaWidget only (flow schedule tab still shows actors below the form) */
  showActorsAndMedia?: boolean;
  /** Narrow inline schedule: show only this widget (tabs live on the slot row). */
  inlineDetailsTab?: ScheduleInlineDetailsTab;
}) => {
  const flow = useFlowStore((s) => s.flow);
  const eventSessionsPlan = useEventStore((s) => s.eventSessionsPlan);
  const eventSessionsOrganize = useEventStore((s) => s.eventSessionsOrganize);
  const mode = useFlowMode();

  // Get session originId from slot.content
  const sessionOriginId = useMemo(() => {
    if (!slot.content) return undefined;
    if (typeof slot.content === 'string') {
      const contentId = slot.content;
      const planMatch = eventSessionsPlan?.find((s) => s.id === contentId);
      const organizeMatch = eventSessionsOrganize?.find((s) => s.id === contentId);
      const matchedSession = planMatch ?? organizeMatch;
      if (matchedSession) {
        return matchedSession.origin ? getObjectId(matchedSession.origin) : matchedSession.id;
      }
      return slot.content;
    }
    return slot.content.origin ? getObjectId(slot.content.origin) : slot.content.id;
  }, [slot, eventSessionsPlan, eventSessionsOrganize]);

  const planSessionFromStore = useMemo(() => {
    if (!sessionOriginId) return undefined;
    return eventSessionsPlan?.find((s) => {
      const sOriginId = s.origin ? getObjectId(s.origin) : s.id;
      return sOriginId === sessionOriginId;
    });
  }, [sessionOriginId, eventSessionsPlan]);

  const organizeSessionFromStore = useMemo(() => {
    if (!sessionOriginId) return undefined;
    return eventSessionsOrganize?.find((s) => {
      const sOriginId = s.origin ? getObjectId(s.origin) : s.id;
      return sOriginId === sessionOriginId;
    });
  }, [sessionOriginId, eventSessionsOrganize]);

  // Fetch from backend to keep session details fresh
  const shouldFetchFromBackend = !!sessionOriginId;
  const shouldFetchOrganize =
    shouldFetchFromBackend && (mode === 'ORGANIZE' || mode === 'PERFORM' || !!organizeSessionFromStore);
  const { data: sessionFromBackendPlan, error: sessionFromBackendPlanError } = useGetSessionQuery(
    shouldFetchFromBackend ? sessionOriginId : undefined,
    'PLAN',
  );
  const { data: sessionFromBackendOrganize } = useGetSessionQuery(
    shouldFetchOrganize ? sessionOriginId : undefined,
    'ORGANIZE',
  );

  const planNotFound =
    mode === 'PLAN' &&
    sessionFromBackendPlanError instanceof ServerError &&
    sessionFromBackendPlanError.statusCode === 404;

  const sessionPlan = useMemo(
    () => pickNewerSession(planSessionFromStore, sessionFromBackendPlan),
    [planSessionFromStore, sessionFromBackendPlan],
  );
  const sessionOrganize = useMemo(
    () => pickNewerSession(organizeSessionFromStore, sessionFromBackendOrganize),
    [organizeSessionFromStore, sessionFromBackendOrganize],
  );

  const backendMode = mode === 'PLAN' ? 'PLAN' : mode === 'ORGANIZE' || mode === 'PERFORM' ? 'ORGANIZE' : undefined;
  const slotSession = useMemo(() => {
    if (slot.content && typeof slot.content === 'object' && 'mode' in slot.content && slot.content.mode) {
      if (slot.content.mode === backendMode) {
        return slot.content;
      }
      if (planNotFound && slot.content.mode === 'ORGANIZE') {
        return slot.content;
      }
    }
    return undefined;
  }, [slot, mode, backendMode, planNotFound]);

  // Determine final session: prefer plan/organize based on mode, fallback to slot
  const session = useMemo(() => {
    if (mode === 'PLAN') {
      return sessionPlan ?? sessionOrganize ?? slotSession;
    }
    if (mode === 'ORGANIZE' || mode === 'PERFORM') {
      return sessionOrganize ?? sessionPlan ?? slotSession;
    }
    return sessionPlan ?? sessionOrganize ?? slotSession;
  }, [mode, sessionPlan, sessionOrganize, slotSession]);

  const navigate = useNavigate();
  const t = useTranslations();

  if (!session || !flow) return null;

  const inlineTabbed = inlineDetailsTab != null;
  const sessionActorsDisplayVariant: ContributorListDisplayVariant | undefined =
    mode === 'PERFORM' ? undefined : 'flowScheduleInline';

  const handleNavigateToSessionList = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    // Get eventId from flow's parent
    const eventId = flow.parent ? (typeof flow.parent === 'object' ? flow.parent.id : flow.parent) : undefined;
    if (eventId) {
      navigate(`/event/${eventId}?tab=sessions&key=${session.id}`);
    } else {
      console.error('Cannot navigate: eventId not found in flow.parent', { flow, session });
    }
  };

  return (
    <Grid container spacing={2}>
      {mode === 'PERFORM' && showActorsAndMedia ? (
        <>
          <Grid size={{ xs: 12 }}>
            <SessionActors session={session} displayVariant={sessionActorsDisplayVariant} />
          </Grid>
          <Grid container direction="column" size={{ xs: 12 }} spacing={2}>
            <SessionPlaylistMediaWidget
              session={session}
              context="Session"
              contextParentId={flow?.origin ? getObjectId(flow.origin) : flow.id}
              contextParentType="Flow"
              enablePlayerSocket={mode === 'PERFORM'}
              enabled={mode === 'PERFORM'}
            />
          </Grid>
        </>
      ) : (
        <>
          <Grid container direction="column" size={{ xs: 12 }} spacing={2}>
            {inlineTabbed ? (
              <>
                {inlineDetailsTab === 'session' && (
                  <UpdateSessionForm
                    session={session}
                    sessionPlan={sessionPlan}
                    sessionOrganize={sessionOrganize}
                    showPublishInHeader
                    onPublishSuccess={onSessionPublishSuccess}
                    modeOverride={mode}
                    alternativeWidgetButton={
                      <Stack direction="row" spacing={0.5}>
                        <SessionToggleLibrary session={session} indicativeOnly={mode !== 'PLAN'} />
                        {session.isInLibrary && (
                          <Tooltip title={t('session.navigateToSessionList')}>
                            <IconButton
                              size="small"
                              onClick={(e) => handleNavigateToSessionList(e)}
                              aria-label="Go to session list"
                            >
                              <Launch fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Stack>
                    }
                  />
                )}
                {inlineDetailsTab === 'actors' && (
                  <SessionActors session={session} displayVariant={sessionActorsDisplayVariant} />
                )}
              </>
            ) : (
              <>
                <UpdateSessionForm
                  session={session}
                  sessionPlan={sessionPlan}
                  sessionOrganize={sessionOrganize}
                  showPublishInHeader
                  onPublishSuccess={onSessionPublishSuccess}
                  modeOverride={mode}
                  alternativeWidgetButton={
                    <Stack direction="row" spacing={0.5}>
                      <SessionToggleLibrary session={session} indicativeOnly={mode !== 'PLAN'} />
                      {session.isInLibrary && (
                        <Tooltip title={t('session.navigateToSessionList')}>
                          <IconButton
                            size="small"
                            onClick={(e) => handleNavigateToSessionList(e)}
                            aria-label="Go to session list"
                          >
                            <Launch fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Stack>
                  }
                />
                <SessionActors session={session} displayVariant={sessionActorsDisplayVariant} />
              </>
            )}
          </Grid>
          {showActorsAndMedia && (
            <Grid container direction="column" size={{ xs: 12 }} spacing={2}>
              <SessionPlaylistMediaWidget
                session={session}
                context="Session"
                contextParentId={flow?.origin ? getObjectId(flow.origin) : flow.id}
                contextParentType="Flow"
                enablePlayerSocket={false}
                enabled={false}
              />
            </Grid>
          )}
        </>
      )}
    </Grid>
  );
};
